import BaseTileLayer from '@arcgis/core/layers/BaseTileLayer';
import TileInfo from '@arcgis/core/layers/support/TileInfo';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Point from '@arcgis/core/geometry/Point';
import Extent from '@arcgis/core/geometry/Extent';

export class BingTileLayer extends BaseTileLayer {
  // Propiedad privada para almacenar el valor del accessor
  private _tileInfo: TileInfo;
  private _fullExtent: Extent;

  urlTemplate: string;
  subDomains: string[];

  constructor(properties?: any) {
    super(properties);
    this.urlTemplate = properties?.urlTemplate || '';
    this.subDomains = properties?.subDomains || ['t0', 't1', 't2', 't3'];

    // Inicializamos los valores por defecto
    this._tileInfo = this._createTileInfo();
    this._fullExtent = new Extent({
      xmin: -20037508.342787,
      ymin: -20037508.342787,
      xmax: 20037508.342787,
      ymax: 20037508.342787,
      spatialReference: { wkid: 102100 },
    });
  }

  // CORRECCIÓN DEFINITIVA: sobrescribimos getter Y setter juntos
  // Esto es obligatorio porque BaseTileLayer los define como Accessor
  override get tileInfo(): TileInfo {
    return this._tileInfo;
  }

  override set tileInfo(value: TileInfo) {
    this._tileInfo = value;
  }

  override get fullExtent(): Extent {
    return this._fullExtent;
  }

  override set fullExtent(value: Extent) {
    this._fullExtent = value;
  }

  private _createTileInfo(): TileInfo {
    return new TileInfo({
      size: [256, 256],
      dpi: 96,
      format: 'jpg',
      spatialReference: new SpatialReference({ wkid: 102100 }),
      origin: new Point({
        x: -20037508.342787,
        y: 20037508.342787,
        spatialReference: { wkid: 102100 },
      }),
      lods: [
        { level: 0, resolution: 156543.033928, scale: 591657527.591555 },
        { level: 1, resolution: 78271.5169639999, scale: 295828763.795777 },
        { level: 2, resolution: 39135.7584820001, scale: 147914381.897889 },
        { level: 3, resolution: 19567.8792409999, scale: 73957190.948944 },
        { level: 4, resolution: 9783.93962049996, scale: 36978595.474472 },
        { level: 5, resolution: 4891.96981024998, scale: 18489297.737236 },
        { level: 6, resolution: 2445.98490512499, scale: 9244648.868618 },
        { level: 7, resolution: 1222.99245256249, scale: 4622324.434309 },
        { level: 8, resolution: 611.49622628138, scale: 2311162.217155 },
        { level: 9, resolution: 305.748113140558, scale: 1155581.108577 },
        { level: 10, resolution: 152.874056570411, scale: 577790.554289 },
        { level: 11, resolution: 76.4370282850732, scale: 288895.277144 },
        { level: 12, resolution: 38.2185141425366, scale: 144447.638572 },
        { level: 13, resolution: 19.1092570712683, scale: 72223.819286 },
        { level: 14, resolution: 9.55462853563415, scale: 36111.909643 },
        { level: 15, resolution: 4.77731426794937, scale: 18055.954822 },
        { level: 16, resolution: 2.38865713397468, scale: 9027.977411 },
        { level: 17, resolution: 1.19432856685505, scale: 4513.988705 },
        { level: 18, resolution: 0.597164283559817, scale: 2256.994353 },
        { level: 19, resolution: 0.298582141647617, scale: 1128.497176 },
      ],
    });
  }

  override getTileUrl(level: number, row: number, col: number): string {
    const quadkey = this._tileXYToQuadKey(col, row, level);
    const subs = this.subDomains;

    const subdomain = subs.length > 0 ? subs[(col + row) % subs.length] : '';

    return this.urlTemplate
      .replace('{q}', quadkey)
      .replace('{subDomain}', subdomain)
      .replace('{subdomain}', subdomain);
  }

  private _tileXYToQuadKey(x: number, y: number, z: number): string {
    let quadkey = '';
    for (let i = z; i > 0; i--) {
      let digit = 0;
      const mask = 1 << (i - 1);
      if ((x & mask) !== 0) digit += 1;
      if ((y & mask) !== 0) digit += 2;
      quadkey += digit.toString();
    }
    return quadkey;
  }
}